import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Settings, LogOut, HelpCircle, CreditCard, Shield } from 'lucide-react';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCredits = (credits: number | undefined) => {
    if (typeof credits !== 'number') return '0.00';
    return credits.toFixed(2);
  };

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <Link to="/dashboard" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="/assets/logo.png" 
              alt="Prestige AI" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
            />
            <span className="text-lg sm:text-xl font-bold text-cyan-600 hidden sm:inline">Prestige AI</span>
          </Link>

          {/* Navigation Items */}
          {user && (
            <div className="flex items-center space-x-2 sm:space-x-4">
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`flex items-center px-2 sm:px-4 py-2 rounded-lg transition-colors ${
                    location.pathname === '/admin'
                      ? 'bg-red-100 text-red-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Shield className="h-5 w-5" />
                  <span className="hidden sm:inline ml-2">Admin</span>
                </Link>
              )}

              <Link
                to="/credits"
                className={`flex items-center px-2 sm:px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/credits'
                    ? 'bg-cyan-100 text-cyan-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <CreditCard className="h-5 w-5" />
                <span className="ml-1 sm:ml-2 font-medium">${formatCredits(user.credits)}</span>
              </Link>

              <Link
                to="/analytics"
                className={`hidden sm:flex items-center px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/analytics' ? 'bg-gray-100' : 'hover:bg-gray-100'
                }`}
              >
                <span className="text-gray-600">Analytics</span>
              </Link>

              <Link
                to="/support"
                className={`p-2 rounded-md transition-colors ${
                  location.pathname === '/support' ? 'bg-gray-100' : 'hover:bg-gray-100'
                }`}
              >
                <HelpCircle className="h-5 w-5 text-gray-600" />
              </Link>

              <Link
                to="/settings"
                className={`p-2 rounded-md transition-colors ${
                  location.pathname === '/settings' ? 'bg-gray-100' : 'hover:bg-gray-100'
                }`}
              >
                <Settings className="h-5 w-5 text-gray-600" />
              </Link>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;